import type {
  DataQualityCategory,
  TeacherContextEntry,
} from "@/services/api/timetableDataQuality";
import type { TimetableGridEntry } from "@/components/timetable-import/typesV2";
import { normalizeTimeSlot } from "@/components/timetable-import/timeUtils";

const DAY_INT_TO_STR: Record<number, string> = {
  0: "MON",
  1: "TUE",
  2: "WED",
  3: "THU",
  4: "FRI",
  5: "SAT",
  6: "SUN",
};

export const DAY_INT_TO_LABEL: Record<number, string> = {
  0: "Mo",
  1: "Di",
  2: "Mi",
  3: "Do",
  4: "Fr",
  5: "Sa",
  6: "So",
};

const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI"];
const DAY_SEQUENCE = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

export function weekDaysForEntries(
  days: Array<number | null | undefined>
): string[] {
  const present = new Set(
    days
      .filter((day): day is number => day != null)
      .map((day) => DAY_INT_TO_STR[day])
      .filter((day): day is string => Boolean(day))
  );
  const result = [...WEEKDAYS];
  for (const extra of ["SAT", "SUN"]) {
    if (present.has(extra)) result.push(extra);
  }
  return result;
}

export function occupiedDays(days: Array<number | null | undefined>): string[] {
  const present = new Set(
    days
      .filter((day): day is number => day != null)
      .map((day) => DAY_INT_TO_STR[day])
      .filter((day): day is string => Boolean(day))
  );
  return DAY_SEQUENCE.filter((day) => present.has(day));
}

type LessonSlot = {
  id: string;
  slot_number: number | null;
  start_time: string | null;
  end_time: string | null;
  is_break?: boolean;
  name?: string | null;
};

const TIME_ADJACENCY_MAX_GAP_MINUTES = 20;

type LabeledSlot = {
  id: string;
  label: string;
  start: number | null;
  end: number | null;
  number: number;
  isBreak: boolean;
};

function entrySlotLabel(entry: TeacherContextEntry): string | null {
  const raw = toGridEntry(entry).time_slot_label;
  if (!raw) return null;
  return normalizeTimeSlot(raw) ?? raw;
}

function fallbackSlotLabel(slot: LessonSlot): string | null {
  if (slot.name && slot.name.trim()) return normalizeTimeSlot(slot.name) ?? slot.name.trim();
  if (slot.start_time && slot.end_time) {
    const raw = `${slot.start_time.slice(0, 5)}-${slot.end_time.slice(0, 5)}`;
    return normalizeTimeSlot(raw) ?? raw;
  }
  if (slot.slot_number != null) return String(slot.slot_number);
  return null;
}

function clockMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match?.[1] || !match[2]) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function byClock(a: LabeledSlot, b: LabeledSlot): number {
  const aTimed = a.start != null;
  const bTimed = b.start != null;
  if (aTimed !== bTimed) return aTimed ? -1 : 1;
  if (a.start != null && b.start != null && a.start !== b.start)
    return a.start - b.start;
  const aEnd = a.end ?? Number.POSITIVE_INFINITY;
  const bEnd = b.end ?? Number.POSITIVE_INFINITY;
  if (aEnd !== bEnd) return aEnd - bEnd;
  return (
    a.label.localeCompare(b.label, "de", { numeric: true }) ||
    a.id.localeCompare(b.id)
  );
}

function chainGap(prev: LabeledSlot, next: LabeledSlot): number | null {
  if (prev.isBreak || next.isBreak) return null;
  if (
    prev.start != null &&
    prev.end != null &&
    next.start != null &&
    next.end != null
  ) {
    if (prev.start === next.start) return null;
    if (next.start < prev.end) return null;
    return next.start - prev.end;
  }
  if (
    prev.start == null &&
    next.start == null &&
    next.number === prev.number + 1
  )
    return 0;
  return null;
}

function crossesBreak(
  prev: LabeledSlot,
  next: LabeledSlot,
  pauses: LabeledSlot[]
): boolean {
  const prevEnd = prev.end;
  const nextStart = next.start;
  if (prevEnd == null || nextStart == null) return false;
  return pauses.some((pause) => {
    if (pause.start == null || pause.end == null) return false;
    return pause.start < nextStart && pause.end > prevEnd;
  });
}

function bellsOf(rows: LabeledSlot[]): LabeledSlot[][] {
  const pauses = rows.filter((row) => row.isBreak);
  const timed = rows
    .filter((row) => !row.isBreak && row.start != null)
    .sort(byClock);
  const untimed = rows
    .filter((row) => !row.isBreak && row.start == null)
    .sort(byClock);
  const bells: LabeledSlot[][] = [];
  for (const slot of timed) {
    let tightestRun = -1;
    let tightestGap = Number.POSITIVE_INFINITY;
    for (let i = 0; i < bells.length; i += 1) {
      const bell = bells[i];
      const last = bell?.[bell.length - 1];
      if (!last) continue;
      const gap = chainGap(last, slot);
      if (gap == null || gap > TIME_ADJACENCY_MAX_GAP_MINUTES) continue;
      if (crossesBreak(last, slot, pauses)) continue;
      // Smallest gap wins: the tightest adjacent run is this bell.
      if (gap < tightestGap) {
        tightestGap = gap;
        tightestRun = i;
      }
    }
    if (tightestRun < 0) bells.push([slot]);
    else bells[tightestRun]?.push(slot);
  }
  let chain: LabeledSlot[] = [];
  const pushChain = (): void => {
    if (chain.length > 0) bells.push(chain);
    chain = [];
  };
  for (const slot of untimed) {
    const prev = chain[chain.length - 1];
    if (prev && slot.number === prev.number + 1) chain.push(slot);
    else {
      pushChain();
      chain = [slot];
    }
  }
  pushChain();
  bells.sort((a, b) => {
    const left = a[0];
    const right = b[0];
    if (!left || !right) return 0;
    return byClock(left, right);
  });
  return bells;
}

function collectAxis(
  entries: TeacherContextEntry[],
  slots?: LessonSlot[] | null
): { rows: LabeledSlot[]; extras: string[] } {
  const byId = new Map<string, string>();
  const lessonOf = new Map<string, number>();
  const entryLabels = new Set<string>();
  for (const entry of entries) {
    const label = entrySlotLabel(entry);
    if (!label) continue;
    entryLabels.add(label);
    if (entry.time_slot_id && !byId.has(entry.time_slot_id))
      byId.set(entry.time_slot_id, label);
    if (entry.lesson_number != null) {
      const prev = lessonOf.get(label);
      if (prev == null || entry.lesson_number < prev)
        lessonOf.set(label, entry.lesson_number);
    }
  }

  const rows: LabeledSlot[] = [];
  const seen = new Set<string>();
  for (const slot of slots ?? []) {
    const isBreak = slot.is_break === true;
    const label = isBreak
      ? null
      : (byId.get(slot.id) ?? fallbackSlotLabel(slot));
    if (!isBreak && (!label || seen.has(label))) continue;
    if (label) seen.add(label);
    rows.push({
      id: slot.id,
      label: label ?? "",
      start: clockMinutes(slot.start_time),
      end: clockMinutes(slot.end_time),
      number: slot.slot_number ?? 999,
      isBreak,
    });
  }

  const extras = [...entryLabels].filter((label) => !seen.has(label));
  extras.sort(
    (a, b) =>
      (lessonOf.get(a) ?? 999) - (lessonOf.get(b) ?? 999) ||
      a.localeCompare(b, "de", { numeric: true })
  );
  return { rows, extras };
}

export function bellOf<T extends LessonSlot>(
  slots: T[],
  currentId: string,
): T[] | null {
  const byId = new Map(slots.map((slot) => [slot.id, slot]));
  const labeled = slots.map((slot) => ({
    id: slot.id,
    label: "",
    start: clockMinutes(slot.start_time),
    end: clockMinutes(slot.end_time),
    number: slot.slot_number ?? 999,
    isBreak: slot.is_break === true,
  }));
  for (const bell of bellsOf(labeled)) {
    if (!bell.some((slot) => slot.id === currentId)) continue;
    return bell.flatMap((slot) => {
      const hit = byId.get(slot.id);
      return hit ? [hit] : [];
    });
  }
  return null;
}

export function neighborSlot<T extends LessonSlot>(
  slots: T[],
  currentId: string,
  direction: "up" | "down",
): T | null {
  const bell = bellOf(slots, currentId);
  if (!bell) return null;
  const index = bell.findIndex((slot) => slot.id === currentId);
  const next = direction === "up" ? index - 1 : index + 1;
  return bell[next] ?? null;
}

export function orderedSlotLabels(
  entries: TeacherContextEntry[],
  slots?: LessonSlot[] | null
): string[] {
  const { rows, extras } = collectAxis(entries, slots);
  return [...rows]
    .filter((row) => !row.isBreak && row.label)
    .sort(byClock)
    .map((row) => row.label)
    .concat(extras);
}

function usedSlotLabels(entries: TeacherContextEntry[]): Set<string> {
  return new Set(
    entries
      .map((entry) => entrySlotLabel(entry))
      .filter((label): label is string => Boolean(label))
  );
}

export function occupiedSlotLabels(
  entries: TeacherContextEntry[],
  slots?: LessonSlot[] | null
): string[] {
  const used = usedSlotLabels(entries);
  return orderedSlotLabels(entries, slots).filter((label) => used.has(label));
}

function contextStub(
  id: string,
  label: string,
  slotId: string | null,
): TeacherContextEntry {
  return {
    id,
    date: null,
    day_of_week: 0,
    time_slot_id: slotId,
    time_slot_name: label,
    start_time: null,
    end_time: null,
    subject_name: null,
    room_name: null,
    klass_name: null,
    lesson_number: null,
    is_recurring: true,
    valid_from: null,
    valid_to: null,
  };
}

export function spanLabelsFromGrid(
  occupiedLabels: Array<string | null | undefined>,
  slots: LessonSlot[],
): string[] {
  const labels = occupiedLabels
    .map((raw) => (raw ? normalizeTimeSlot(raw) ?? raw.trim() : null))
    .filter((label): label is string => Boolean(label));
  if (!labels.length || !slots.length) return [];
  const byLabel = new Map<string, string>();
  for (const slot of slots) {
    if (slot.is_break) continue;
    if (slot.name) {
      const name = normalizeTimeSlot(slot.name) ?? slot.name.trim();
      if (name) byLabel.set(name, slot.id);
    }
    const clock = slot.start_time && slot.end_time
      ? normalizeTimeSlot(`${slot.start_time.slice(0, 5)}-${slot.end_time.slice(0, 5)}`)
      : null;
    if (clock) byLabel.set(clock, slot.id);
  }
  return slotSpanLabels(
    labels.map((label, index) => contextStub(`span-${index}`, label, byLabel.get(label) ?? null)),
    slots,
  );
}

export function spanClockAxis(
  occupied: string[],
  slots: Array<{
    id: string;
    start: string;
    end: string;
    slot_number?: number | null;
    is_break?: boolean;
  }>,
): string[] {
  if (!occupied.length || !slots.length) return occupied;
  const keyOf = (start: string, end: string) => `${start}–${end || start}`;
  const lessonSlots: LessonSlot[] = slots.map((slot) => ({
    id: slot.id,
    slot_number: slot.slot_number ?? null,
    start_time: slot.start ? `${slot.start.length === 5 ? slot.start : slot.start.slice(0, 5)}:00` : null,
    end_time: slot.end ? `${slot.end.length === 5 ? slot.end : slot.end.slice(0, 5)}:00` : null,
    is_break: slot.is_break === true,
    name: slot.is_break ? null : keyOf(slot.start, slot.end),
  }));
  const idByKey = new Map(
    lessonSlots.filter((slot) => slot.name).map((slot) => [slot.name as string, slot.id]),
  );
  const slotKeys = new Set(idByKey.keys());
  const occupiedSet = new Set(occupied);
  const back = new Map<string, string>();
  for (const slot of lessonSlots) {
    if (!slot.name) continue;
    back.set(slot.name, slot.name);
    const normalized = normalizeTimeSlot(slot.name);
    if (normalized) back.set(normalized, slot.name);
  }
  for (const key of occupied) {
    back.set(key, key);
    const normalized = normalizeTimeSlot(key);
    if (normalized && !back.has(normalized)) back.set(normalized, key);
  }
  const restore = (label: string): string | null => {
    const key = back.get(label);
    if (!key || (!slotKeys.has(key) && !occupiedSet.has(key))) return null;
    return key;
  };
  const groups = spanGroups(
    occupied.map((key, index) => contextStub(`axis-${index}`, key, idByKey.get(key) ?? null)),
    lessonSlots,
  );
  const bells: string[][] = [];
  const loose: string[] = [];
  for (const group of groups) {
    const restored = group.flatMap((label) => {
      const key = restore(label);
      return key ? [key] : [];
    });
    const slotRows = restored.filter((key) => slotKeys.has(key));
    if (slotRows.length) bells.push([...new Set(slotRows)]);
    loose.push(...restored.filter((key) => !slotKeys.has(key)));
  }
  for (const key of occupied) {
    if (!slotKeys.has(key)) loose.push(key);
  }
  const placed = new Set<string>();
  for (const key of loose) {
    if (placed.has(key) || slotKeys.has(key)) continue;
    placed.add(key);
    const start = clockMinutes(key.split("–")[0] ?? "");
    if (start == null) continue;
    let best = -1;
    let bestDist = Number.POSITIVE_INFINITY;
    bells.forEach((bell, index) => {
      const starts = bell
        .map((row) => clockMinutes(row.split("–")[0] ?? ""))
        .filter((value): value is number => value != null);
      if (!starts.length) return;
      const ends = bell
        .map((row) => clockMinutes(row.split("–")[1] || row.split("–")[0]))
        .filter((value): value is number => value != null);
      const min = Math.min(...starts);
      const max = ends.length ? Math.max(...ends) : Math.max(...starts);
      if (start < min - TIME_ADJACENCY_MAX_GAP_MINUTES || start > max + TIME_ADJACENCY_MAX_GAP_MINUTES) return;
      const dist = Math.min(...starts.map((value) => Math.abs(value - start)));
      if (dist < bestDist) {
        bestDist = dist;
        best = index;
      }
    });
    if (best >= 0) bells[best]?.push(key);
    else bells.push([key]);
  }
  const untimed = [...new Set(loose.filter((key) => clockMinutes(key.split("–")[0] ?? "") == null))];
  if (!bells.length) return [...new Set([...occupied])];
  return [...bells.flat(), ...untimed];
}

function spanGroups(
  entries: TeacherContextEntry[],
  slots?: LessonSlot[] | null,
): string[][] {
  const used = usedSlotLabels(entries);
  if (used.size === 0) return [];
  const { rows, extras } = collectAxis(entries, slots);
  const groups: string[][] = [];
  for (const bell of bellsOf(rows)) {
    let first = -1;
    let last = -1;
    bell.forEach((slot, index) => {
      if (!used.has(slot.label)) return;
      if (first < 0) first = index;
      last = index;
    });
    if (first < 0) continue;
    const labels: string[] = [];
    for (let index = first; index <= last; index += 1) {
      const slot = bell[index];
      if (slot) labels.push(slot.label);
    }
    if (labels.length) groups.push(labels);
  }
  const extraLabels = extras.filter((extra) => used.has(extra));
  if (extraLabels.length) groups.push(extraLabels);
  return groups;
}

export function slotSpanLabels(
  entries: TeacherContextEntry[],
  slots?: LessonSlot[] | null
): string[] {
  return spanGroups(entries, slots).flat();
}

export function toGridEntry(entry: TeacherContextEntry): TimetableGridEntry {
  let timeSlotLabel = entry.time_slot_name ?? null;
  if (!timeSlotLabel && entry.start_time && entry.end_time) {
    timeSlotLabel = `${entry.start_time.substring(0, 5)}-${entry.end_time.substring(0, 5)}`;
  }
  return {
    id: entry.id,
    teacher_code: null,
    day_of_week:
      entry.day_of_week !== undefined && entry.day_of_week !== null
        ? (DAY_INT_TO_STR[entry.day_of_week] ?? null)
        : null,
    time_slot_label: timeSlotLabel,
    lesson_number: entry.lesson_number,
    subject: entry.subject_name ?? null,
    room: entry.room_name ?? null,
    class_name: entry.klass_name ?? null,
    is_multi_period: false,
    lesson_count: 1,
    multi_period_position: null,
    substitution_excluded: entry.substitution_excluded ?? false,
    ampel: entry.ampel ?? null,
    ampel_reasons: entry.ampel_reasons,
  };
}

export function entryMatchesGroup(
  entry: {
    subject_name: string | null;
    room_name: string | null;
    klass_name: string | null;
    subject_id?: string | null;
    room_id?: string | null;
    klass_id?: string | null;
    subject_ids?: string[] | null;
  },
  category: DataQualityCategory,
  isNullValue: boolean,
  value: string | null
): boolean {
  if (
    category === "subject" &&
    entry.subject_ids &&
    entry.subject_ids.length > 0
  )
    return false;
  const linkedId =
    category === "subject"
      ? entry.subject_id
      : category === "room"
        ? entry.room_id
        : entry.klass_id;
  if (linkedId) return false;
  const raw =
    category === "subject"
      ? entry.subject_name
      : category === "room"
        ? entry.room_name
        : entry.klass_name;
  const trim = (raw ?? "").trim();
  if (isNullValue) return trim === "";
  return trim.toLowerCase() === (value ?? "").trim().toLowerCase();
}
